import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { supabase } from '../../services/supabase';
import type { Club } from '../../types/models';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminRole } from '../../contexts/AdminRoleContext';
import { fixTypes } from '../../services/adminPermissions';
import { apiCache } from '../../services/apiCache';

// Cache keys
const CLUBS_CACHE_KEY = 'admin_clubs_list';
const CLUBS_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

const ClubsAdmin: React.FC = () => {
  const { user } = useAuth();
  const { userRole, isSuperAdmin, loading: roleLoading } = useAdminRole();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [clubName, setClubName] = useState('');
  const [editingClubId, setEditingClubId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Fetch clubs when component mounts or userRole changes
  useEffect(() => {
    // Only fetch clubs when we have user role data
    if (!roleLoading) {
      fetchClubs();
    }
  }, [roleLoading, userRole?.id]); // Only re-fetch if user role ID changes

  const fetchClubs = async () => {
    setLoading(true);
    try {
      // Build cache key specific to the user's role
      const cacheKey = userRole 
        ? `${CLUBS_CACHE_KEY}_${userRole.id}_${userRole.role}` 
        : CLUBS_CACHE_KEY;
      
      // Get from cache or fetch
      const clubsData = await apiCache.getOrFetch<Club[]>(
        cacheKey,
        async () => {
          
          let queryResult;
          
          if (isSuperAdmin) {
            // Superadmins can see all clubs
            queryResult = await supabase
              .from('clubs')
              .select('*')
              .order('name', { ascending: true });
          } else if (userRole?.club_id) {
            // Regular admins can only see their assigned club
            queryResult = await supabase
              .from('clubs')
              .select('*')
              .eq('id', userRole.club_id)
              .order('name', { ascending: true });
          } else {
            // User doesn't have club access
            return [];
          }
          
          const { data, error } = queryResult;
          
          if (error) {
            throw error;
          }
          
          // Fix types to ensure proper typing
          return fixTypes<Club>(data || []);
        },
        CLUBS_CACHE_TTL
      );
      
      setClubs(clubsData);
    } catch (error: any) {
      console.error('Error fetching clubs:', error.message);
      showSnackbar('Failed to load clubs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (club?: Club) => {
    if (club) {
      setEditingClubId(club.id);
      setClubName(club.name);
    } else {
      setEditingClubId(null);
      setClubName('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setClubName('');
    setEditingClubId(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const clearClubsCache = () => {
    // Clear cache for all club-related keys
    // Use a more sophisticated approach if needed for multiple users
    apiCache.clear();
  };

  const handleSubmit = async () => {
    if (!clubName.trim()) {
      showSnackbar('Club name is required', 'error');
      return;
    }

    try {
      if (editingClubId) {
        // Update existing club
        const { error } = await supabase
          .from('clubs')
          .update({ name: clubName })
          .eq('id', editingClubId);

        if (error) throw error;
        showSnackbar('Club updated successfully', 'success');
      } else {
        // Create new club
        const { error } = await supabase
          .from('clubs')
          .insert([{ name: clubName }]);

        if (error) throw error;
        showSnackbar('Club created successfully', 'success');
      }

      // Clear cache after modifications
      clearClubsCache();
      
      handleCloseDialog();
      fetchClubs();
    } catch (error: any) {
      console.error('Error saving club:', error.message);
      showSnackbar(`Failed to ${editingClubId ? 'update' : 'create'} club`, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this club? This will also delete all associated leagues.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('clubs')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Clear cache after modifications
      clearClubsCache();
      
      showSnackbar('Club deleted successfully', 'success');
      fetchClubs();
    } catch (error: any) {
      console.error('Error deleting club:', error.message);
      showSnackbar('Failed to delete club', 'error');
    }
  };

  // Show loading if we're waiting for role data
  if (roleLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          Manage Clubs
        </Typography>
        {isSuperAdmin && (
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ borderRadius: 2 }}
          >
            Add Club
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : clubs.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography>No clubs found. Create your first club!</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Club Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clubs.map((club) => (
                <TableRow key={club.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    {club.name}
                  </TableCell>
                  <TableCell align="right">
                    {/* Only allow editing for all admins */}
                    <IconButton 
                      onClick={() => handleOpenDialog(club)} 
                      color="primary" 
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    {/* Only superadmins can delete clubs */}
                    {isSuperAdmin && (
                      <IconButton 
                        onClick={() => handleDelete(club.id)} 
                        color="error" 
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog for adding/editing club */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingClubId ? 'Edit Club' : 'Add New Club'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Club Name"
            type="text"
            fullWidth
            variant="outlined"
            value={clubName}
            onChange={(e) => setClubName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} color="primary" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="secondary"
            sx={{ borderRadius: 2 }}
          >
            {editingClubId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClubsAdmin;